/**
 * Google Analytics 4 Service
 *
 * Comprehensive tracking service implementing the GA4_TRACKING_SPECIFICATION.md
 *
 * Event naming convention: {category}_{action}_{target}
 * Categories: auth_, property_, filter_, nav_, user_, billing_, error_
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    __ga4Events?: Array<{ name: string; params: any }>; // For testing
  }
}

// ============================================================================
// Types
// ============================================================================

export type UserTier = 'STARTER' | 'PRO' | 'ELITE' | 'UNLIMITED';
export type Strategy = 'btl' | 'flip' | 'hmo' | 'brrr' | 'rent_to_rent' | 'bmv';
export type PropertyType = 'house' | 'flat' | 'bungalow' | 'maisonette' | 'other';
export type ViewSource = 'list' | 'map' | 'favourite' | 'search' | 'direct';
export type PriceBand = 'under_100k' | '100k_200k' | '200k_300k' | '300k_500k' | '500k_1m' | 'over_1m';

export interface PropertyParams {
  property_id: string;
  property_type?: string;
  region?: string;
  postcode_sector?: string;
  strategy?: string;
  price_band?: string;
  bedrooms?: number;
  is_unlocked?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Simple hash function for anonymizing user IDs
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Hash user ID for privacy - never send raw user IDs to GA4
 */
export function hashUserId(userId: string): string {
  return `usr_${simpleHash(userId).substring(0, 12)}`;
}

/**
 * Get price band from numeric price
 */
export function getPriceBand(price: number): PriceBand {
  if (price < 100000) return 'under_100k';
  if (price < 200000) return '100k_200k';
  if (price < 300000) return '200k_300k';
  if (price < 500000) return '300k_500k';
  if (price < 1000000) return '500k_1m';
  return 'over_1m';
}

/**
 * Extract postcode sector from full postcode (privacy: remove specific location)
 */
export function getPostcodeSector(postcode: string | undefined): string {
  if (!postcode) return 'unknown';
  return postcode.split(' ')[0] || postcode.substring(0, 4);
}

/**
 * Sanitize text to remove any potential PII (emails, etc.)
 */
export function sanitizeText(text: string): string {
  // Remove email patterns
  return text.replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[REDACTED]');
}

/**
 * Get yield band from yield percentage
 */
export function getYieldBand(yieldPercent: number): string {
  if (yieldPercent < 5) return 'under_5';
  if (yieldPercent < 6) return '5_6';
  if (yieldPercent < 7) return '6_7';
  if (yieldPercent < 8) return '7_8';
  if (yieldPercent < 10) return '8_10';
  return 'over_10';
}

// ============================================================================
// GA4 Service Class
// ============================================================================

class GA4Service {
  private isEnabled: boolean;
  private debugMode: boolean;
  private measurementId: string | undefined;

  constructor() {
    this.measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    this.debugMode = process.env.NODE_ENV === 'development';
    this.isEnabled = !!(
      this.measurementId &&
      typeof window !== 'undefined' &&
      window.gtag
    );
  }

  /**
   * Internal method to send events
   */
  private track(eventName: string, params?: Record<string, any>) {
    // For testing: store events in window object
    if (typeof window !== 'undefined' && window.__ga4Events) {
      window.__ga4Events.push({ name: eventName, params });
    }

    if (!this.isEnabled) {
      if (this.debugMode) {
        console.log('[GA4 Debug]', eventName, params);
      }
      return;
    }

    window.gtag?.('event', eventName, {
      ...params,
      debug_mode: this.debugMode
    });
  }

  // ==========================================================================
  // User Identification
  // ==========================================================================

  /**
   * Set user ID (hashed for privacy)
   */
  setUser(userId: string | null, properties?: { tier?: UserTier }) {
    if (!this.isEnabled) return;

    if (userId) {
      const hashedId = hashUserId(userId);
      window.gtag?.('config', this.measurementId!, {
        user_id: hashedId,
      });

      if (properties) {
        this.setUserProperties(properties);
      }
    }
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.isEnabled) return;

    window.gtag?.('set', 'user_properties', properties);
  }

  /**
   * Clear user on logout
   */
  clearUser() {
    if (!this.isEnabled) return;

    window.gtag?.('config', this.measurementId!, {
      user_id: null,
    });
  }

  // ==========================================================================
  // Authentication Events (auth_)
  // ==========================================================================

  authLoginStart(method: 'email' | 'google' | 'apple' = 'email') {
    this.track('auth_login_start', { method });
  }

  authLoginSuccess(method: 'email' | 'google' | 'apple' = 'email', userTier?: UserTier) {
    this.track('auth_login_success', { method, user_tier: userTier });
  }

  authLoginFailure(method: 'email' | 'google' | 'apple' = 'email', errorType?: string) {
    this.track('auth_login_failure', { method, error_type: errorType });
  }

  authLogout() {
    this.track('auth_logout');
  }

  authSignupStart() {
    this.track('auth_signup_start');
  }

  authSignupComplete(userTier: UserTier = 'STARTER') {
    this.track('auth_signup_complete', { user_tier: userTier });
  }

  authSignupFailure(errorType?: string) {
    this.track('auth_signup_failure', { error_type: errorType });
  }

  authPasswordReset() {
    this.track('auth_password_reset');
  }

  authEmailVerify() {
    this.track('auth_email_verify');
  }

  // ==========================================================================
  // Property Events (property_)
  // ==========================================================================

  propertyView(params: PropertyParams & { source?: ViewSource }) {
    this.track('property_view', {
      property_id: params.property_id,
      property_type: params.property_type,
      region: params.region,
      postcode_sector: params.postcode_sector ? getPostcodeSector(params.postcode_sector) : undefined,
      strategy: params.strategy,
      price_band: params.price_band,
      bedrooms: params.bedrooms,
      is_unlocked: params.is_unlocked,
      source: params.source || 'direct'
    });
  }

  propertyUnlock(params: PropertyParams & { credits_used?: number; credits_remaining?: number }) {
    this.track('property_unlock', {
      property_id: params.property_id,
      property_type: params.property_type,
      region: params.region,
      postcode_sector: params.postcode_sector ? getPostcodeSector(params.postcode_sector) : undefined,
      strategy: params.strategy,
      price_band: params.price_band,
      credits_used: params.credits_used || 1,
      credits_remaining: params.credits_remaining
    });
  }

  propertyFavouriteAdd(params: PropertyParams & { source?: 'card' | 'detail' | 'map' }) {
    this.track('property_favourite_add', {
      property_id: params.property_id,
      property_type: params.property_type,
      region: params.region,
      source: params.source || 'card'
    });
  }

  propertyFavouriteRemove(params: PropertyParams & { source?: 'card' | 'detail' | 'map' }) {
    this.track('property_favourite_remove', {
      property_id: params.property_id,
      property_type: params.property_type,
      region: params.region,
      source: params.source || 'card'
    });
  }

  propertyShareStart(propertyId: string) {
    this.track('property_share_start', { property_id: propertyId });
  }

  propertyShareComplete(propertyId: string, recipientIsMember: boolean) {
    this.track('property_share_complete', {
      property_id: propertyId,
      recipient_is_member: recipientIsMember,
      share_method: 'email'
    });
  }

  propertyPdfExport(params: PropertyParams & {
    has_crime_data?: boolean;
    has_poi_data?: boolean;
    strategies_count?: number;
    primary_strategy?: string;
    has_normalized_data?: boolean;
    export_source?: 'deal_card' | 'deal_detail';
  }) {
    this.track('property_pdf_export', {
      property_id: params.property_id,
      property_type: params.property_type,
      region: params.region,
      has_crime_data: params.has_crime_data,
      has_poi_data: params.has_poi_data,
      strategies_count: params.strategies_count,
      primary_strategy: params.primary_strategy,
      has_normalized_data: params.has_normalized_data,
      export_source: params.export_source,
    });
  }

  propertyPdfStrategyFallback(params: {
    property_id: string;
    requested_strategy: string;
    resolved_strategy: string;
    export_source: 'deal_card' | 'deal_detail';
  }) {
    this.track('property_pdf_strategy_fallback', params);
  }

  propertyListView(propertyId: string) {
    this.track('property_list_view', { property_id: propertyId });
  }

  propertyImageView(propertyId: string, imageIndex: number) {
    this.track('property_image_view', { property_id: propertyId, image_index: imageIndex });
  }

  /** Track strategy tab selection on deal detail page */
  propertyStrategySelect(propertyId: string, strategy: string, previousStrategy?: string) {
    this.track('property_strategy_select', {
      property_id: propertyId,
      strategy,
      previous_strategy: previousStrategy,
    });
  }

  /** Track score breakdown expansion on deal detail page */
  propertyScoreBreakdownView(propertyId: string, strategy: string) {
    this.track('property_score_breakdown_view', {
      property_id: propertyId,
      strategy,
    });
  }

  // ==========================================================================
  // Filter Events (filter_)
  // ==========================================================================

  filterApply(filterName: string, filterCategory: string) {
    // Note: Do NOT include filter values - could contain sensitive data
    this.track('filter_apply', {
      filter_name: filterName,
      filter_category: filterCategory
    });
  }

  filterClearAll() {
    this.track('filter_clear_all');
  }

  filterClearSingle(filterName: string) {
    this.track('filter_clear_single', { filter_name: filterName });
  }

  filterPresetSave(presetName: string) {
    this.track('filter_preset_save', { preset_name: sanitizeText(presetName) });
  }

  filterPresetLoad(presetName: string) {
    this.track('filter_preset_load', { preset_name: sanitizeText(presetName) });
  }

  filterPresetDelete(presetName: string) {
    this.track('filter_preset_delete', { preset_name: sanitizeText(presetName) });
  }

  filterPanelOpen() {
    this.track('filter_panel_open');
  }

  filterPanelClose() {
    this.track('filter_panel_close');
  }

  filterCategoryExpand(category: string) {
    this.track('filter_category_expand', { category });
  }

  filterCategoryCollapse(category: string) {
    this.track('filter_category_collapse', { category });
  }

  // ==========================================================================
  // Navigation Events (nav_)
  // ==========================================================================

  navPageView(pagePath: string, pageTitle?: string) {
    this.track('nav_page_view', {
      page_path: pagePath,
      page_title: pageTitle || document.title
    });
  }

  navTabChange(tabName: string, previousTab?: string) {
    this.track('nav_tab_change', {
      tab_name: tabName,
      previous_tab: previousTab
    });
  }

  navSortChange(sortOption: string, previousOption?: string, resultsCount?: number) {
    this.track('nav_sort_change', {
      sort_option: sortOption,
      previous_option: previousOption,
      results_count: resultsCount
    });
  }

  navViewModeChange(viewMode: 'list' | 'map', previousMode?: 'list' | 'map') {
    this.track('nav_view_mode_change', {
      view_mode: viewMode,
      previous_mode: previousMode
    });
  }

  navMapMarkerClick(propertyId: string) {
    this.track('nav_map_marker_click', { property_id: propertyId });
  }

  navMapZoom(zoomLevel: number) {
    this.track('nav_map_zoom', { zoom_level: zoomLevel });
  }

  navScrollLoadMore(pageNumber: number, itemsLoaded: number) {
    this.track('nav_scroll_load_more', {
      page_number: pageNumber,
      items_loaded: itemsLoaded
    });
  }

  navExternalLink(destination: 'external' | 'source' | 'other', propertyId?: string) {
    this.track('nav_external_link', {
      destination,
      property_id: propertyId
    });
  }

  navSidebarToggle(isOpen: boolean) {
    this.track('nav_sidebar_toggle', { is_open: isOpen });
  }

  // ==========================================================================
  // User Events (user_)
  // ==========================================================================

  userPreferencesUpdate(preferencesChanged: string[]) {
    this.track('user_preferences_update', {
      preferences_changed: preferencesChanged.join(',')
    });
  }

  userFeedbackSubmit(feedbackType: 'feedback' | 'feature' | 'bug') {
    // Note: Do NOT include subject or message content
    this.track('user_feedback_submit', { feedback_type: feedbackType });
  }

  userNotificationView(notificationType: string) {
    this.track('user_notification_view', { notification_type: notificationType });
  }

  userNotificationClick(notificationType: string, action: string) {
    this.track('user_notification_click', {
      notification_type: notificationType,
      action
    });
  }

  userNotificationDismiss(notificationType: string) {
    this.track('user_notification_dismiss', { notification_type: notificationType });
  }

  userReferralShare(method: 'copy' | 'email' | 'social') {
    this.track('user_referral_share', { share_method: method });
  }

  userTourStart() {
    this.track('user_tour_start');
  }

  userTourComplete() {
    this.track('user_tour_complete');
  }

  userTourSkip(stepNumber: number) {
    this.track('user_tour_skip', { step_number: stepNumber });
  }

  // ==========================================================================
  // Billing Events (billing_)
  // ==========================================================================

  billingCheckoutStart(itemType: 'subscription' | 'credits', itemName: string) {
    this.track('billing_checkout_start', {
      item_type: itemType,
      item_name: itemName
    });
  }

  billingCheckoutComplete(itemType: 'subscription' | 'credits', itemName: string, value: number) {
    this.track('billing_checkout_complete', {
      item_type: itemType,
      item_name: itemName,
      value,
      currency: 'GBP'
    });
  }

  billingCheckoutAbandon(itemType: 'subscription' | 'credits', itemName: string) {
    this.track('billing_checkout_abandon', {
      item_type: itemType,
      item_name: itemName
    });
  }

  billingSubscriptionChange(fromTier: UserTier, toTier: UserTier) {
    this.track('billing_subscription_change', {
      from_tier: fromTier,
      to_tier: toTier
    });
  }

  billingSubscriptionCancel(tier: UserTier) {
    this.track('billing_subscription_cancel', { tier });
  }

  billingCreditsPurchase(packName: string, creditsAmount: number, value: number) {
    this.track('billing_credits_purchase', {
      pack_name: packName,
      credits_amount: creditsAmount,
      value,
      currency: 'GBP'
    });
  }

  billingCreditsLow(creditsRemaining: number) {
    this.track('billing_credits_low', { credits_remaining: creditsRemaining });
  }

  // ==========================================================================
  // Error Events (error_)
  // ==========================================================================

  errorApi(errorCode: number, errorType: string, endpoint: string) {
    // Note: Do NOT include error messages - could contain PII
    this.track('error_api', {
      error_code: errorCode,
      error_type: errorType,
      endpoint
    });
  }

  errorValidation(fieldName: string, validationType: string) {
    this.track('error_validation', {
      field_name: fieldName,
      validation_type: validationType
    });
  }

  errorAuth(errorType: 'token_expired' | 'unauthorized' | 'session_invalid') {
    this.track('error_auth', { error_type: errorType });
  }

  errorPayment(errorType: string) {
    this.track('error_payment', { error_type: errorType });
  }

  errorNetwork() {
    this.track('error_network');
  }

  // ==========================================================================
  // Performance Events (perf_) - Beta Testing
  // ==========================================================================

  /**
   * Track page load performance
   */
  perfPageLoad(params: {
    page_path: string;
    load_time_ms: number;
    first_contentful_paint?: number;
    time_to_interactive?: number;
  }) {
    this.track('perf_page_load', params);
  }

  /**
   * Track slow API responses (>3s)
   */
  perfApiSlow(params: {
    endpoint: string;
    response_time_ms: number;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  }) {
    this.track('perf_api_slow', params);
  }

  /**
   * Track slow image loads (>2s)
   */
  perfImageLoadSlow(params: {
    image_type: 'property' | 'map' | 'avatar' | 'other';
    load_time_ms: number;
  }) {
    this.track('perf_image_load_slow', params);
  }

  /**
   * Track map rendering performance
   */
  perfMapRender(params: {
    marker_count: number;
    render_time_ms: number;
  }) {
    this.track('perf_map_render', params);
  }

  /**
   * Track search/filter execution time
   */
  perfSearchTime(params: {
    filter_count: number;
    results_count: number;
    duration_ms: number;
  }) {
    this.track('perf_search_time', params);
  }

  // ==========================================================================
  // Security Events (security_) - Beta Testing
  // ==========================================================================

  /**
   * Track potential brute force attempts
   */
  securityAuthBruteForce(params: {
    attempt_count: number;
    time_window_seconds: number;
  }) {
    this.track('security_auth_brute_force', params);
  }

  /**
   * Track token expiration
   */
  securityTokenExpired(params: {
    session_duration_minutes: number;
    action_attempted: string;
  }) {
    this.track('security_token_expired', params);
  }

  /**
   * Track unauthorized access attempts
   */
  securityPermissionDenied(params: {
    resource_type: string;
    action: string;
  }) {
    this.track('security_permission_denied', params);
  }

  /**
   * Track rate limit hits
   */
  securityRateLimitHit(params: {
    endpoint: string;
    limit_type: 'user' | 'ip' | 'global';
  }) {
    this.track('security_rate_limit_hit', params);
  }

  /**
   * Track suspicious activity patterns
   */
  securitySuspiciousActivity(params: {
    activity_type: string;
    risk_score: number;
  }) {
    this.track('security_suspicious_activity', params);
  }

  /**
   * Track session anomalies (potential hijacking)
   */
  securitySessionHijackAttempt(params: {
    ip_changed: boolean;
    user_agent_changed: boolean;
  }) {
    this.track('security_session_hijack_attempt', params);
  }

  /**
   * Track XSS attempt in input
   */
  securityXssAttempt(params: {
    field_name: string;
    page_path: string;
  }) {
    this.track('security_xss_attempt', params);
  }

  /**
   * Track CORS errors
   */
  securityCorsError(params: {
    origin: string;
    endpoint: string;
  }) {
    this.track('security_cors_error', params);
  }

  // ==========================================================================
  // System Health Events (system_) - Beta Testing
  // ==========================================================================

  /**
   * Track uncaught JavaScript errors
   */
  systemJsError(params: {
    error_name: string;
    component?: string;
    stack_hash: string;
    page_path: string;
  }) {
    this.track('system_js_error', params);
  }

  /**
   * Track unhandled promise rejections
   */
  systemPromiseRejection(params: {
    reason_type: string;
    component?: string;
  }) {
    this.track('system_promise_rejection', params);
  }

  /**
   * Track React error boundary triggers
   */
  systemComponentCrash(params: {
    component_name: string;
    page_path: string;
    error_name?: string;
  }) {
    this.track('system_component_crash', params);
  }

  /**
   * Track API timeouts
   */
  systemApiTimeout(params: {
    endpoint: string;
    timeout_ms: number;
  }) {
    this.track('system_api_timeout', params);
  }

  /**
   * Track WebSocket disconnections
   */
  systemWebsocketDisconnect(params: {
    reconnect_attempt: number;
    duration_connected: number;
  }) {
    this.track('system_websocket_disconnect', params);
  }

  /**
   * Track storage errors (localStorage/sessionStorage)
   */
  systemStorageError(params: {
    storage_type: 'local' | 'session' | 'indexed_db';
    operation: 'read' | 'write' | 'delete';
  }) {
    this.track('system_storage_error', params);
  }

  /**
   * Track external service degradation
   */
  systemServiceDegraded(params: {
    service_name: string;
    status_code?: number;
    response_time_ms?: number;
  }) {
    this.track('system_service_degraded', params);
  }

  /**
   * Track high memory usage
   */
  systemMemoryWarning(params: {
    heap_used_mb: number;
    heap_limit_mb: number;
  }) {
    this.track('system_memory_warning', params);
  }

  // ==========================================================================
  // UX Issues Events (ux_) - Beta Testing
  // ==========================================================================

  /**
   * Track rage clicks (frustration indicator)
   */
  uxRageClick(params: {
    element_type: string;
    click_count: number;
    page_path: string;
  }) {
    this.track('ux_rage_click', params);
  }

  /**
   * Track dead clicks (clicks on non-interactive elements)
   */
  uxDeadClick(params: {
    element_type: string;
    page_path: string;
  }) {
    this.track('ux_dead_click', params);
  }

  /**
   * Track scroll bounce (immediate scroll up after load)
   */
  uxScrollBounce(params: {
    page_path: string;
    time_to_bounce_ms: number;
  }) {
    this.track('ux_scroll_bounce', params);
  }

  /**
   * Track form abandonment
   */
  uxFormAbandon(params: {
    form_name: string;
    fields_completed: number;
    time_spent_seconds: number;
  }) {
    this.track('ux_form_abandon', params);
  }

  /**
   * Track slow interactions (>500ms response)
   */
  uxSlowInteraction(params: {
    element_type: string;
    interaction_type: 'click' | 'input' | 'scroll' | 'hover';
    delay_ms: number;
  }) {
    this.track('ux_slow_interaction', params);
  }

  /**
   * Track double form submissions
   */
  uxDoubleSubmit(params: {
    form_name: string;
    submit_count: number;
  }) {
    this.track('ux_double_submit', params);
  }

  /**
   * Track back button exits
   */
  uxBackButtonExit(params: {
    page_path: string;
    time_on_page_seconds: number;
  }) {
    this.track('ux_back_button_exit', params);
  }

  /**
   * Track help/tooltip access
   */
  uxHelpAccessed(params: {
    help_type: 'tooltip' | 'modal' | 'link' | 'faq';
    context: string;
    page_path: string;
  }) {
    this.track('ux_help_accessed', params);
  }

  // ==========================================================================
  // Feature Adoption Events (feature_) - Beta Testing
  // ==========================================================================

  /**
   * Track first use of a feature
   */
  featureFirstUse(params: {
    feature_name: string;
    days_since_signup: number;
  }) {
    this.track('feature_first_use', params);
  }

  /**
   * Track feature discovery (tooltip/hint shown)
   */
  featureDiscovery(params: {
    feature_name: string;
    trigger: 'auto' | 'hover' | 'click' | 'onboarding';
  }) {
    this.track('feature_discovery', params);
  }

  /**
   * Track ignored CTAs
   */
  featureIgnored(params: {
    feature_name: string;
    times_shown: number;
  }) {
    this.track('feature_ignored', params);
  }

  /**
   * Track advanced feature usage
   */
  featureDepth(params: {
    feature_name: string;
    depth_level: number;
  }) {
    this.track('feature_depth', params);
  }

  /**
   * Track beta feature toggle
   */
  featureBetaToggle(params: {
    feature_name: string;
    enabled: boolean;
  }) {
    this.track('feature_beta_toggle', params);
  }

  /**
   * Track keyboard shortcut usage
   */
  featureKeyboardShortcut(params: {
    shortcut_key: string;
    action: string;
  }) {
    this.track('feature_keyboard_shortcut', params);
  }

  // ==========================================================================
  // Session Quality Events (session_) - Beta Testing
  // ==========================================================================

  /**
   * Track session start with extended data
   */
  sessionStartExtended(params: {
    referrer_type: 'direct' | 'search' | 'social' | 'email' | 'referral' | 'other';
    landing_page: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  }) {
    this.track('session_start_extended', params);
  }

  /**
   * Track engagement milestones
   */
  sessionEngagementMilestone(params: {
    minutes_engaged: number;
    pages_viewed: number;
    actions_taken: number;
  }) {
    this.track('session_engagement_milestone', params);
  }

  /**
   * Track return from idle
   */
  sessionIdleReturn(params: {
    idle_duration_seconds: number;
  }) {
    this.track('session_idle_return', params);
  }

  /**
   * Track tab visibility changes
   */
  sessionTabVisibility(params: {
    visibility_state: 'visible' | 'hidden';
    hidden_duration_seconds?: number;
  }) {
    this.track('session_tab_visibility', params);
  }

  /**
   * Track device orientation change
   */
  sessionDeviceOrientation(params: {
    orientation: 'portrait' | 'landscape';
    page_path: string;
  }) {
    this.track('session_device_orientation', params);
  }

  /**
   * Track connection type change
   */
  sessionConnectionChange(params: {
    connection_type: '4g' | '3g' | '2g' | 'slow-2g' | 'wifi' | 'ethernet' | 'unknown';
    downlink_mbps?: number;
  }) {
    this.track('session_connection_change', params);
  }

  // ==========================================================================
  // Form Analytics Events (form_) - Beta Testing
  // ==========================================================================

  /**
   * Track form field validation error
   */
  formFieldError(params: {
    form_name: string;
    field_name: string;
    error_type: string;
  }) {
    this.track('form_field_error', params);
  }

  /**
   * Track form field help shown
   */
  formFieldHelp(params: {
    form_name: string;
    field_name: string;
  }) {
    this.track('form_field_help', params);
  }

  /**
   * Track field retry after error
   */
  formFieldRetry(params: {
    form_name: string;
    field_name: string;
    retry_count: number;
  }) {
    this.track('form_field_retry', params);
  }

  /**
   * Track browser autofill
   */
  formAutoFill(params: {
    form_name: string;
    fields_autofilled: number;
  }) {
    this.track('form_auto_fill', params);
  }

  /**
   * Track copy/paste in form fields
   */
  formCopyPaste(params: {
    form_name: string;
    field_name: string;
    action: 'copy' | 'paste';
  }) {
    this.track('form_copy_paste', params);
  }

  /**
   * Track form completion time
   */
  formCompletionTime(params: {
    form_name: string;
    duration_seconds: number;
    field_count: number;
  }) {
    this.track('form_completion_time', params);
  }

  // ==========================================================================
  // Content Engagement Events (content_) - Beta Testing
  // ==========================================================================

  /**
   * Track scroll depth milestones
   */
  contentScrollDepth(params: {
    page_path: string;
    depth_percent: 25 | 50 | 75 | 90 | 100;
    content_type: string;
  }) {
    this.track('content_scroll_depth', params);
  }

  /**
   * Track read time
   */
  contentReadTime(params: {
    content_type: string;
    duration_seconds: number;
    completion_percent: number;
  }) {
    this.track('content_read_time', params);
  }

  /**
   * Track accordion/section toggle
   */
  contentExpandCollapse(params: {
    section_name: string;
    action: 'expand' | 'collapse';
    page_path: string;
  }) {
    this.track('content_expand_collapse', params);
  }

  /**
   * Track image zoom/expand
   */
  contentZoomImage(params: {
    image_type: string;
    zoom_level?: number;
  }) {
    this.track('content_zoom_image', params);
  }

  /**
   * Track text copy to clipboard
   */
  contentCopyText(params: {
    content_type: string;
    char_count: number;
  }) {
    this.track('content_copy_text', params);
  }

  /**
   * Track print dialog
   */
  contentPrintAttempt(params: {
    page_path: string;
    content_type: string;
  }) {
    this.track('content_print_attempt', params);
  }

  // ==========================================================================
  // Support Events (support_) - Beta Testing
  // ==========================================================================

  /**
   * Track help chat open
   */
  supportChatOpen(params: {
    page_path: string;
    trigger: 'button' | 'auto' | 'link';
  }) {
    this.track('support_chat_open', params);
  }

  /**
   * Track FAQ view
   */
  supportFaqView(params: {
    faq_category: string;
    question_id: string;
  }) {
    this.track('support_faq_view', params);
  }

  /**
   * Track contact form open
   */
  supportContactFormOpen(params: {
    page_path: string;
    issue_type?: string;
  }) {
    this.track('support_contact_form_open', params);
  }

  /**
   * Track documentation view
   */
  supportDocumentationView(params: {
    doc_category: string;
    doc_id: string;
  }) {
    this.track('support_documentation_view', params);
  }

  /**
   * Track help video play
   */
  supportVideoPlay(params: {
    video_id: string;
    duration_seconds?: number;
  }) {
    this.track('support_video_play', params);
  }

  /**
   * Track support content search
   */
  supportSearch(params: {
    search_has_results: boolean;
    result_count: number;
  }) {
    this.track('support_search', params);
  }

  // ==========================================================================
  // Experiment Events (experiment_) - Beta Testing
  // ==========================================================================

  /**
   * Track experiment exposure
   */
  experimentExposure(params: {
    experiment_id: string;
    variant_id: string;
  }) {
    this.track('experiment_exposure', params);
  }

  /**
   * Track experiment conversion
   */
  experimentConversion(params: {
    experiment_id: string;
    variant_id: string;
    goal_name: string;
  }) {
    this.track('experiment_conversion', params);
  }

  /**
   * Track experiment interaction
   */
  experimentInteraction(params: {
    experiment_id: string;
    variant_id: string;
    interaction_type: string;
  }) {
    this.track('experiment_interaction', params);
  }

  // ==========================================================================
  // Utility Methods
  // ==========================================================================

  /**
   * Check if GA4 is enabled
   */
  get enabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Track a custom event (for any not covered above)
   */
  customEvent(eventName: string, params?: Record<string, any>) {
    this.track(eventName, params);
  }

  /**
   * Legacy method for backward compatibility
   */
  pageView(pagePath: string, pageTitle?: string) {
    this.navPageView(pagePath, pageTitle);
  }

  /**
   * Legacy method for backward compatibility
   */
  event(eventName: string, params?: Record<string, any>) {
    this.track(eventName, params);
  }

  /**
   * Legacy conversion method
   */
  conversion(eventName: string, value?: number, currency: string = 'GBP') {
    this.track(eventName, { value, currency });
  }
}

// Export singleton instance
export const ga4 = new GA4Service();

// Export helper functions for use in components
export { simpleHash };
